import {
  BudgetDashboardFiltersDto,
  BudgetDashboardGroupItem,
  BudgetDashboardMonthlyItem,
  BudgetDashboardOptions,
  BudgetDashboardProjectResponse,
  BudgetDashboardSummary,
  BlacklistItem,
  DashboardFiltersDto,
} from "../types/dashboard";
import { api } from "./apiClient";

export default class DashboardService {
  static async getStatusCount(): Promise<any> {
    return api
      .get("/dashboard/status-counts")
      .then((response) => response.data);
  }

  static async getProjectStatusCount(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/project-status-count", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getSummary(filters: DashboardFiltersDto, signal?: AbortSignal) {
    return api
      .get("/dashboard/summary", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getBillingVsRevenue(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ) {
    return api
      .get("/dashboard/billing-vs-revenue", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getProjects(filters: DashboardFiltersDto, signal?: AbortSignal) {
    return api
      .get("/dashboard/projects", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getUpcomingEvents(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ) {
    return api
      .get("/dashboard/upcoming-events", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getCEODashboardData(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ) {
    return api
      .get("/dashboard/ceo-dashboard", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getEventControlOptions(
    filters?: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/event-control/options", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getEventControlRankings(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/event-control/rankings", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getEventControlSummary(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ) {
    return api
      .get("/dashboard/event-control/summary", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getEventControlMmsByMonth(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/event-control/mms-by-month", { params: filters, signal })
      .then((response) => response.data);
  }

  static async getEventControlMonthlyCards(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/event-control/monthly-cards", {
        params: filters,
        signal,
      })
      .then((response) => response.data);
  }

  static async getEventControlUpcomingEvents(
    filters: DashboardFiltersDto,
    signal?: AbortSignal
  ): Promise<any> {
    return api
      .get("/dashboard/event-control/upcoming-events", {
        params: filters,
        signal,
      })
      .then((response) => response.data);
  }

  static async getBudgetDashboardProjects(
    filters: BudgetDashboardFiltersDto
  ): Promise<BudgetDashboardProjectResponse> {
    return api
      .get("/dashboard/budgets/projects", { params: filters })
      .then((response) => response.data);
  }

  static async getBudgetDashboardSummary(
    filters: BudgetDashboardFiltersDto
  ): Promise<BudgetDashboardSummary> {
    return api
      .get("/dashboard/budgets/summary", { params: filters })
      .then((response) => response.data);
  }

  static async getBudgetDashboardMonthly(
    filters: BudgetDashboardFiltersDto
  ): Promise<BudgetDashboardMonthlyItem[]> {
    return api
      .get("/dashboard/budgets/monthly", { params: filters })
      .then((response) => response.data);
  }

  static async getBudgetDashboardOptions(): Promise<BudgetDashboardOptions> {
    return api
      .get("/dashboard/budgets/options")
      .then((response) => response.data);
  }

  static async getBudgetDashboardByCell(
    filters: BudgetDashboardFiltersDto
  ): Promise<BudgetDashboardGroupItem[]> {
    return api
      .get("/dashboard/budgets/by-cell", { params: filters })
      .then((response) => response.data);
  }

  static async getBudgetDashboardByClient(
    filters: BudgetDashboardFiltersDto
  ): Promise<BudgetDashboardGroupItem[]> {
    return api
      .get("/dashboard/budgets/by-client", { params: filters })
      .then((response) => response.data);
  }

  static async downloadDebugXlsx(sessionId: string): Promise<void> {
    const baseUrl = process.env.REACT_APP_HOST || "http://localhost:3100";
    const token = localStorage.getItem("@mmeventos:access_token");

    const response = await fetch(
      `${baseUrl}/dashboard/debug/download-xlsx?sessionId=${encodeURIComponent(
        sessionId
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Falha ao baixar debug: ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-debug-${sessionId.slice(0, 8)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async getBlacklist(): Promise<BlacklistItem[]> {
    return api
      .get("/dashboard/budgets/blacklist")
      .then((response) => response.data);
  }

  static async addToBlacklist(clientId: string): Promise<BlacklistItem> {
    return api
      .post("/dashboard/budgets/blacklist", { clientId })
      .then((response) => response.data);
  }

  static async removeFromBlacklist(clientId: string): Promise<void> {
    return api
      .delete(`/dashboard/budgets/blacklist/${clientId}`)
      .then((response) => response.data);
  }
}
